import { observer } from 'mobx-react';

import Icon from '../../../app/shared/components/Icon/Icon';
import InputSearch from '../../../app/shared/components/InputSearch/InputSearch';
import ThreeDotsLoader from '../../../app/shared/components/Loaders/ThreeDotsLoader';
import ResultsCounter from '../../../app/shared/components/ResultsCounter/ResultsCounter';
import { PricelistListingEmptyTranslation, PricelistListTranslation } from '../../localization/ModuleTranslation';
import type { CustomerPricelistData } from '../../model';
import ConfirmationModal from '../../shared/ConfirmationModal/ConfirmationModal';
import PricelistFormModal from '../../shared/PricelistFormModal/PricelistFormModal';
import Button from '../../ui/Button/Button';
import LoadingStatus from '../../ui/LoadingStatus/LoadingStatus';
import LoadMore from '../../ui/LoadMore/LoadMore';
import css from './PricelistListing.css';
import { PricelistListingItem, PricelistListingItemSkeleton } from './PricelistListingItem/PricelistListingItem';
import PricelistListingItemState from './PricelistListingItem/PricelistListingItemState';
import PricelistListingState from './PricelistListingState';

interface PricelistListingProps {
  state: PricelistListingState;
}

const PricelistListing = observer(({ state }: PricelistListingProps) => {
  const { translation } = state;

  const isLoadingInitialData = state.loadingState === 'loading';
  const isLoading = isLoadingInitialData || state.loadingState === 'loadingMore';

  return (
    <div className={css.root}>
      <div className={css.header}>
        <search className={css.search}>
          <InputSearch
            defaultValue={state.searchTerm}
            placeholderTranslation={translation.searchPlaceholder}
            onValueChange={state.setSearchTerm}
          />
        </search>
        <ResultsCounter translation={translation.listsFoundLabel} value={state.total} />
        <PricelistFormModal
          state={state.createPricelistModal}
          trigger={
            <Button className={css.newItemButton} theme="primary" variant="outline">
              <Icon className={css.icon} name="filePlus" />
              {state.translation.createPricelistModalTriggerLabel}
            </Button>
          }
        />
      </div>
      {isLoading && (
        <LoadingStatus message={isLoadingInitialData ? translation.loadingLabel : translation.loadingMoreLabel} />
      )}
      {isLoadingInitialData ? (
        <PricelistListSkeleton />
      ) : (
        <>
          <PricelistList
            items={state.items}
            translation={translation.pricelistList}
            onCreateItem={() => state.createPricelistModal.open()}
            onEditItem={(pricelist) => state.editPricelistModal.open(pricelist)}
            onDeleteItem={(pricelist) => state.deletePricelist(pricelist)}
          />
          {state.canLoadMorePricelists && (
            <LoadMore
              isLoading={state.loadingState === 'loadingMore'}
              scrollOffset={0.5}
              onLoadMore={state.loadMorePricelists}
            >
              <div className={css.loadingMore}>
                <ThreeDotsLoader />
              </div>
            </LoadMore>
          )}
        </>
      )}
      <PricelistFormModal state={state.editPricelistModal} />
      <ConfirmationModal state={state.confirmationModal} />
    </div>
  );
});

export default PricelistListing;

/** Placeholder cards for the first load: two rows of the grid, enough to fill the fold. */
const SKELETON_CARD_COUNT = 6;

const PricelistListSkeleton = () => {
  return (
    <div className={css.list}>
      {Array.from({ length: SKELETON_CARD_COUNT }, (_: unknown, index: number) => (
        <PricelistListingItemSkeleton key={index} className={css.item} />
      ))}
    </div>
  );
};

interface PricelistListProps {
  items: PricelistListingItemState[];
  translation: PricelistListTranslation;
  onCreateItem: () => void;
  onEditItem: (pricelist: CustomerPricelistData) => void;
  onDeleteItem: (pricelist: CustomerPricelistData) => void;
}

const PricelistList = observer(({ items, onCreateItem, onDeleteItem, onEditItem, translation }: PricelistListProps) => {
  if (items.empty()) {
    return <PricelistListingEmpty translation={translation.pricelistListingEmpty} onCreateItem={onCreateItem} />;
  }

  return (
    <ul className={css.list}>
      {items.map((i) => (
        <li key={i.data.id}>
          <PricelistListingItem
            className={css.item}
            state={i}
            actions={[
              { label: translation.itemActions.edit.label, action: () => onEditItem(i.data) },
              { label: translation.itemActions.delete.label, action: () => onDeleteItem(i.data) },
            ]}
          />
        </li>
      ))}
    </ul>
  );
});

interface PricelistListingEmptyProps {
  translation: PricelistListingEmptyTranslation;
  onCreateItem: () => void;
}

const PricelistListingEmpty = ({ onCreateItem, translation }: PricelistListingEmptyProps) => {
  return (
    <div className={css.empty}>
      {translation.message}
      <button type="button" className={css.button} onClick={onCreateItem}>
        {translation.createLabel}
      </button>
    </div>
  );
};
